import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { firebaseConnect, getVal } from 'react-redux-firebase'
import {
  compose,
  getContext,
  lifecycle,
  withHandlers,
  withState,
  setDisplayName
} from 'recompose'
import { setCropBlock } from '../../../store/actions/selectedBlocks'
import { doneWithLocalImage } from '../../../store/actions/upload'
import {
  cropBlockIdSelector,
  colWidthSelector
} from '../../../store/selectors/boardSelectors'
import { imgixRedirect, placeholders } from 'variables'
import { placementWidthSelector } from '../../../store/selectors/selectors'
import { BoardWrapper } from '../BoardContainer';

const BlockImageContainer = compose(
  getContext({ router: PropTypes.object }),
  firebaseConnect(({ block }) =>
    block.image.pendingUpload
      ? [`/pendingUploads/${block.image.pendingUpload}`]
      : []
  ),
  withState('usePlaceholder', 'setUsePlaceholder', null),
  connect(
    (state, { block, layout, location, usePlaceholder, router }) => {
      const cropping = cropBlockIdSelector(state) === block.id
      const props = {
        name: block.name,
        cropping,
        crop: block.image.cropOverride || block.image.crop
      }
      if (block.image.src) {
        // handle data-uris
        if (block.image.src.substr(0, 5) === 'data:') {
          props.src = { src: block.image.src }
        } else if (location === 'queue') {
          const colWidth = colWidthSelector(state)
          const placementWidth = placementWidthSelector(state)
          props.src = { src: getImgixSrc(block, placementWidth * colWidth) }
        } else {
          if (usePlaceholder !== null) {
            props.src = usePlaceholder
              ? {
                  src: getImgixSrc(block, layout.width, true),
                  isPlaceholder: true
                }
              : { src: getImgixSrc(block, layout.width) }
          } else {
            // if coming from the board (to single block) show the board-sized image until full size loads
            if (
              router.history.location.state &&
              router.history.location.state.fromBoard
            ) {
              const colWidth = colWidthSelector(state)
              props.src = {
                src: getImgixSrc(block, block.width * colWidth)
              }
            }
          }
        }
      } else if (block.image.pendingUpload) {
        if (
          state.uploads[block.image.pendingUpload] &&
          state.uploads[block.image.pendingUpload].localUrl
        ) {
          props.src = {
            src: state.uploads[block.image.pendingUpload].localUrl,
            fixOrientation: fixOrientation(block.image)
          }
        }
        props.uploading = true
        const pendingUpload = getVal(
          state.firebase.data,
          `/pendingUploads/${block.image.pendingUpload}`
        )
        if (pendingUpload) {
          const { complete, size } = pendingUpload
          if (complete && size && complete !== size)
            props.uploadProgress = complete / size
        }
      }
      return props
    },
    (dispatch, { block }) => ({
      cropStart: () => dispatch(setCropBlock(block.id)),
      cropDone: () => dispatch(setCropBlock(null)),
      doneWithLocalImage: src => dispatch(doneWithLocalImage(src))
    })
  ),
  withState('loaded', 'setLoaded', false),
  withState('previousSrc', 'setPreviousSrc'),
  lifecycle({
    componentWillReceiveProps({ block: nextBlock, src: nextSrc }) {
      const { block, src, setPreviousSrc, setLoaded } = this.props
      if (block.id !== nextBlock.id && nextSrc.src !== src.src) {
        setPreviousSrc(null)
        setLoaded(false)
      } else if (nextSrc && src && nextSrc.src !== src.src) {
        setPreviousSrc(src)
        setLoaded(false)
      }
    }
  }),
  withHandlers({
    toggleCrop: ({ cropping, cropStart, cropDone }) =>
      cropping ? cropDone : cropStart,
    onLoad: ({
      setLoaded,
      previousSrc,
      setPreviousSrc,
      doneWithLocalImage
    }) => () => {
      setLoaded(true)
      // TODO this can cause setState after unmount
      return setTimeout(() => {
        setPreviousSrc(null)
        if (previousSrc) doneWithLocalImage(previousSrc.src)
      }, placeholders.fadeTime * 1000)
    },
    observe: ({ setUsePlaceholder, location }) => ref => {
      if (location !== 'board') {
        setUsePlaceholder(false)
        return
      }
      if (ref) {
        // TODO: one observer for whole board?
        const observer = new IntersectionObserver(
          (entries, observer) => {
            const entry = entries.find(entry => entry.target === ref)
            if (entry) {
              if (entry.isIntersecting) {
                setUsePlaceholder(false)
                observer.disconnect()
              } else {
                setUsePlaceholder(true)
              }
            }
          },
          {
            root: document.querySelector(BoardWrapper.toString()),
            rootMargin: '0% 0% 50% 0%'
          }
        )
        observer.observe(ref)
      }
    }
  }),
  setDisplayName('BlockImageContainer')
)

// Utils

const fixOrientation = image => {
  const aspect = image.aspect || 1
  switch (image.orientation) {
    case 2:
      return { transform: `scaleX(-1)` }
    case 3:
      return { transform: `rotate(180deg)` }
    case 4:
      return { transform: `scaleY(-1)` }
    case 5:
      return {
        transform: `rotate(90deg) scale(${aspect}, ${-aspect})`,
        transformOrigin: '0 0'
      }
    case 6:
      return {
        transform: `rotate(90deg) scale(${aspect}) translateY(-100%)`,
        transformOrigin: '0 0'
      }
    case 7:
      return {
        transform: `rotate(270deg) scale(${aspect}, ${-aspect})`,
        transformOrigin: '100% 0%'
      }
    case 8:
      return {
        transform: `rotate(270deg) scale(${aspect}) translateX(-100%)`,
        transformOrigin: '0 0'
      }
    default:
  }
}

const getImgixSrc = (block, width, placeholder) => {
  if (block.image.crop) width = width * (block.image.crop.scale || 1)
  const dpr = window.devicePixelRatio || 1
  let imgixParams = {
    auto: 'compress,format',
    w: Math.min(block.image.width / dpr, Math.ceil(width / 200) * 200),
    dpr
  }
  if (placeholder) {
    imgixParams.w /= placeholders.downscale
    imgixParams.fm = 'jpg' // fallback from auto:format if browser doesn't support webP
    imgixParams.q = 20
  }
  const src = encodeURIComponent(block.image.src)
  imgixParams = Object.keys(imgixParams)
    .map(k => k + '=' + encodeURIComponent(imgixParams[k]))
    .join('&')
  imgixParams = encodeURIComponent(imgixParams)
  return `${imgixRedirect}?path=${src}&imgixParams=%3F${imgixParams}`
}

export default BlockImageContainer
